import { useCallback, useEffect, useMemo, useState } from "react";

// Component Imports
import { AGTable } from "@/components";
import DeleteModal from "@/components/Modals/DeleteModal/DeleteModal";

// Hooks Imports
import { useAGTable } from "./hooks/useAGTable";
import { useDeleteStrategyMutation, useFetchAllStrategiesQuery } from "../../services/strategyManagementApi";
import { useAlerts } from "@/hooks";

// Types Imports
import { IAGStrategyDataRow } from "./StrategyTable.types";

// Utils Imports
import { getErrorMessage } from "@/utils/getErrorMessage";
import { mapRowData } from "./utils/mapRowData";

const StrategyTable = () => {
  // States - START --------------------------------------------------------------
  const { showError } = useAlerts();
  const [deleteModalStatus, setDeleteModalStatus] = useState<{
    open: boolean;
    row: IAGStrategyDataRow | null;
  }>({ open: false, row: null });
  // States - END ----------------------------------------------------------------

  // Hooks - START --------------------------------------------------------------
  const { data: strategies, isLoading: isLoadingStrategies, isError: isErrorStrategies, error: errorStrategies } = useFetchAllStrategiesQuery();
  const [deleteStrategy] = useDeleteStrategyMutation();

  const rowData = useMemo(
    () => (strategies ? mapRowData(strategies) : []),
    [strategies]
  );

  const closeDeleteModal = useCallback(() => {
    setDeleteModalStatus({ open: false, row: null });
  }, []);

  const onDeleteStrategy = useCallback((row: IAGStrategyDataRow) => {
    setDeleteModalStatus({ open: true, row });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    const row = deleteModalStatus.row;
    if (!row) return;
    try {
      await deleteStrategy({ id: row.id }).unwrap();
      closeDeleteModal();
    } catch (err) {
      showError(`Failed to delete strategy: ${getErrorMessage(err)}`);
    }
  }, [deleteModalStatus.row, deleteStrategy, showError, closeDeleteModal]);

  const {
    gridRef,
    colDefs,
    defaultColDef,
    getContextMenuItems,
    gridContext,
  } = useAGTable({ onDeleteStrategy });
  // Hooks - END ----------------------------------------------------------------

  // useEffect - START ----------------------------------------------------------
  useEffect(() => {
    if (isErrorStrategies) {
      showError(`Failed to fetch strategies: ${getErrorMessage(errorStrategies)}`);
    }
  }, [isErrorStrategies, errorStrategies, showError]);

  // Sync refetched data into the grid (AG Grid with getRowId doesn't always update from rowData prop)
  useEffect(() => {
    gridRef.current?.api?.setGridOption("rowData", rowData ?? []);
  }, [rowData]);
  // useEffect - END ------------------------------------------------------------

  return (
    <>
      <AGTable
        gridRef={gridRef}
        rowData={rowData}
        colDefs={colDefs}
        defaultColDef={defaultColDef}
        groupDisplayType="singleColumn"
        sideBar={false}
        animateRows={false}
        loading={isLoadingStrategies}
        getContextMenuItems={getContextMenuItems}
        tableHeight={750}
        suppressCellFocus
        suppressColumnVirtualisation
        suppressRowVirtualisation
        suppressRowHoverHighlight
        debounceVerticalScrollbar
        context={gridContext}
      />

      {deleteModalStatus.open && (
        <DeleteModal
          open
          item_name={deleteModalStatus.row?.display_name ?? deleteModalStatus.row?.strategy_id ?? "this strategy"}
          handleClose={closeDeleteModal}
          handleSubmit={handleConfirmDelete}
        />
      )}
    </>
  )
}

export default StrategyTable;